import { camelCaseKeys } from "../case"
import { ClientState } from "../shell"
import { isArray, isString } from "../utils/utils"
import { OmniaApiState } from "./apiState"
import { HoldingEvent, HoldingSession } from "./types"

type Subscription = (type: string, data: any) => void

interface Message<T> {
    type: string,
    data: T
}

/** @internal */
export class OmniaApiConnection {

    private socket: WebSocket

    private reconnectDelay = 0
    private reconnectTimeoutEvent = 0
    private readyStateInterval = 0

    private readonly subscribers: Record<string, Subscription[]> = {}

    private _sendQueue = [] as string[]

    constructor(
        private readonly apiBaseUrl: string,
        private readonly state: OmniaApiState,
        private readonly shouldRetryConnections: () => boolean,
        private readonly options: {
            waitOnMasterDataUpdateNotifications: boolean
        }
    ) {

        this.apiBaseUrl = apiBaseUrl.replace(/^http/, "ws")

        this.subscribers = {
            sessions: [(_, d) => this.updateSessions(d)],
            events: [(_, d) => this.mergeEvents(d)],
            "master-data": [(_, d) => this.updateMasterData(d)]
        }

    }

    isOpen() {
        return this.socket?.readyState == WebSocket.OPEN
    }

    public open(sessionId: string, authToken: string) {

        this.close()

        const url = `${this.apiBaseUrl}/api/ws/v1/connect?sessionId=${sessionId}&authToken=${encodeURIComponent(authToken)}`

        // console.log("apiconnection.open", url)

        const socket = this.socket = new WebSocket(url)

        socket.onmessage = m => {

            this.reconnectDelay = 0

            const msg = JSON.parse(m.data) as { Type: string, Data: any }

            if (msg.Type) {

                const parts = msg.Type.split('/')

                for (let index = parts.length; index > 0; index--) {

                    const scope = parts.slice(0, index).join('/')
                    const type = parts.slice(index).join('/')
                    const sub = this.subscribers[scope]

                    if (isArray(sub)) {
                        for (const s of sub) {
                            s(type, msg.Data)
                        }
                    }

                }

            }

        }

        const handleConnectionLost = () => {

            if (!this.shouldRetryConnections()) {
                return
            }

            this.reconnectDelay = Math.min(60, Math.max(5, this.reconnectDelay * 2))

            // console.log("apiconnection.handleConnectionLost", this.reconnectDelay)

            const h = this.reconnectTimeoutEvent = window.setTimeout(() => {
                if (h === this.reconnectTimeoutEvent) {
                    this.open(sessionId, authToken)
                }
            }, this.reconnectDelay * 1000)

        }

        socket.onopen = () => {

            const h = this.readyStateInterval = window.setInterval(() => {

                if ((h === this.reconnectTimeoutEvent) && (socket.readyState !== WebSocket.OPEN)) {
                    // console.log("apiconnection.readyState", socket.readyState)
                    handleConnectionLost()
                }

            }, 10000)

            socket.onopen = undefined

            this.sendPending()

        }

        socket.onclose = m => {

            if (m.code > 1001) {
                console.log("onclose", m)
                handleConnectionLost()
            }

        }

    }

    /**
     * Subscribes to notification through a callback function
     * @param type Type of message to subscribe to
     * @param handler Message handler
     * @returns Unsuscription function
     */
    subscribe<T>(type: string, handler: (type: string, data: T) => void, convertToCamelCase: boolean = true) {

        const { subscribers } = this

        const array = subscribers[type] ?? (subscribers[type] = [])

        const actualHandler = convertToCamelCase
            ? (type: string, data: any) => handler(type, camelCaseKeys(data) as T)
            : handler

        array.push(actualHandler)

        return () => {
            const array = subscribers[type]
            array?.remove(p => p === actualHandler)
        }
    }

    /**
     * Listens to one message of a specific type
     * @param type Type of message to list to
     * @returns Promise resolved when a message of the requested type is received
     */
    listen<T>(type: string, convertToCamelCase: boolean = true): Promise<Message<T>> {

        return new Promise<Message<T>>((resolve) => {

            const dispose = this.subscribe<T>(type, (type, data) => {
                resolve({ type, data })
                dispose?.()
            }, convertToCamelCase)

        })

    }

    private sendPending() {

        const q = this._sendQueue
        this._sendQueue = []

        for (const i of q) {
            this.send(i)
        }

    }

    private send(data: any) {

        if (!isString(data)) {
            data = JSON.stringify(data)
        }

        if (this.socket.readyState != WebSocket.OPEN) {
            this._sendQueue.push(data)
        }
        else {
            this.socket.send(data)
        }

    }

    public close() {

        const { socket, reconnectTimeoutEvent, readyStateInterval } = this

        this.socket = undefined
        this.reconnectTimeoutEvent = undefined
        this.readyStateInterval = undefined

        if (readyStateInterval) {
            clearInterval(readyStateInterval)
        }

        if (reconnectTimeoutEvent) {
            clearTimeout(reconnectTimeoutEvent)
        }

        if (socket) {
            socket.onclose = undefined
            socket.close()
        }

    }

    mergeEvents(data: HoldingEvent[]) {
        this.state.mergeEvents(data)
    }

    updateSessions(data: HoldingSession[]) {
        this.state.updateSessions(data)
    }

    sendClientState(state: ClientState) {

        const message = {
            Type: "setSessionState",
            Data: state
        }

        this.send(message)

    }


    private updateMasterDataTimeoutEvent = 0

    /**
     * This is called when receiving a "master-data" update notification
     * (i.e., when master data is updated)
     */
    private updateMasterData(update: { year: number, datasets: string[] }) {

        // These notifications are sent to all connected clients, and there can be many of them
        // Calling state.updateMasterData() will in turn trigger multiple API requests,
        // which could flood the API, so we are waiting a bit of random time, between 10 and 20 seconds,
        // before doing so
        const delay = this.options.waitOnMasterDataUpdateNotifications
            ? (1 + Math.random()) * 10000
            : 10

        this.updateMasterDataTimeoutEvent ||= window.setTimeout(_ => {

            this.updateMasterDataTimeoutEvent = 0
            this.state.updateMasterData(update)

        }, delay)


    }

}
